import {useState, useEffect, useCallback} from 'react'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {PageLayout} from '@/components/shared/PageLayout'
import {useNavigate} from 'react-router-dom'
import {useMutation} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {CONCEPT_ICON} from '@/lib/concepts'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useDialog} from '@/hooks/useDialog'
import type {WizardStep} from '@/components/shared/WizardStepper'
import {PersonStep} from '@/features/onboarding/steps/PersonStep'
import {OrganizationStep} from '@/features/onboarding/steps/OrganizationStep'
import {IdentityStep} from '@/features/onboarding/steps/IdentityStep'
import {ReviewStep} from '@/features/onboarding/steps/ReviewStep'
import {useOnboardingForm} from '@/features/onboarding/hooks/useOnboardingForm'
import {requiredAttrsAreFilled} from '@/features/onboarding/state/requiredAttrsCheck'
import {useFormLayout} from '@/hooks/useFormLayout'
import {useAppConfig} from '@/hooks/use-app-config'
import {useServices} from '@/services/context'
import {OnboardingStep} from '@/features/onboarding/types'
import type {OnboardingRequest, OnboardingResponse} from '@/features/onboarding/types'
import type {OnboardingRequest as ServiceOnboardingRequest} from '@/types/onboarding'
import {AppLocalDate, formatDateOnly} from '@/lib/date-utils'
import {IdentityLifecycleState} from '@/types/identity'

const TOTAL_STEPS = 4
const PARTY_ATTRIBUTE_USE_CASE_KEY = 'party-attribute'

/**
 * User Onboarding Wizard — 4-step guided flow.
 * Step 0: Person, Step 1: Organization, Step 2: Identity, Step 3: Review.
 */
export function OnboardingWizard() {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const {toast} = useToast()
    const {
        form,
        update,
        updateMultiple,
        setTemplate,
        setIdentifiers,
        setPartyAttributes,
        setIdentityAttributes,
        clearTemplateSwitched,
        reset,
        canProceedFrom,
    } = useOnboardingForm()

    const [step, setStep] = useState(0)
    const cancelDialog = useDialog()
    const [hasIdentifierDuplicate, setHasIdentifierDuplicate] = useState(false)

    const STEP_LABELS: WizardStep[] = [
        {id: 'person', title: i18n.onboarding.steps.person as string},
        {id: 'organization', title: i18n.onboarding.steps.organization as string},
        {id: 'identity-step', title: i18n.onboarding.steps.identity as string},
        {id: 'review', title: i18n.onboarding.steps.review as string},
    ]

    // beforeunload listener
    useEffect(() => {
        if (!form.dirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [form.dirty])

    // Focus management on step transition
    useEffect(() => {
        // Find first input in current step and focus it
        const timer = setTimeout(() => {
            const container = document.querySelector('[data-wizard-step]')
            if (container) {
                // eslint-disable-next-line i18next/no-literal-string -- DOM selector
                const firstInput = container.querySelector('input, select, textarea') as HTMLElement | null
                firstInput?.focus()
            }
        }, 100)
        return () => clearTimeout(timer)
    }, [step])

    // Submit mutation
    const submitMutation = useMutation({
        mutationFn: (request: OnboardingRequest) => services.onboarding.submit(request as unknown as ServiceOnboardingRequest),
        onSuccess: (response: OnboardingResponse) => {
            const isAwaitingEntry = response.lifecycleState === IdentityLifecycleState.SUSPENDED
                && !!form.entryDate
                && form.entryDate.substring(0, 10) > AppLocalDate.today()
            let message = i18n.onboarding.messages.success
            if (isAwaitingEntry && form.entryDate) {
                message = `${message}. ${i18n.onboarding.messages.successAwaitingEntry({entryDate: formatDateOnly(form.entryDate)})}`
            }
            toast({description: message})
            navigate(routes.identityManagement.identities.detail(response.identityId))
        },
    })

    const handleNext = useCallback(() => {
        if (step < TOTAL_STEPS - 1) {
            if (form.templateSwitched) clearTemplateSwitched()
            setStep(s => s + 1)
        }
    }, [step, form.templateSwitched, clearTemplateSwitched])

    const handlePrevious = useCallback(() => {
        if (step > 0) setStep(s => s - 1)
    }, [step])

    const handleSubmit = useCallback(() => {
        const request: OnboardingRequest = {
            partyId: form.existingPartyId,
            partyVersion: form.existingPartyVersion,
            partyType: form.mode === 'new' ? form.partyType : null,
            displayName: form.mode === 'new' ? form.displayName : null,
            relationships: form.relationships
                .filter(r => r.targetId)
                .map(r => ({relationshipType: r.relationshipType, targetIdentityId: r.targetId!})),
            partyAttributes: form.partyAttributes,
            identityType: form.identityType,
            entryDate: form.entryDate || null,
            exitDate: form.exitDate || null,
            identityAttributes: form.identityAttributes,
            identifiers: form.identifiers.map(r => ({
                identifierType: r.identifierType,
                identifierValue: r.identifierValue,
                primary: r.primary,
            })),
        }
        submitMutation.mutate(request)
    }, [form, submitMutation])

    const handleCancel = useCallback(() => {
        cancelDialog.open()
    }, [cancelDialog])

    const handleDiscard = useCallback(() => {
        cancelDialog.close()
        reset()
        setStep(0)
    }, [reset, cancelDialog])

    const {data: partyAttributeLayout} = useFormLayout(PARTY_ATTRIBUTE_USE_CASE_KEY)
    const {partyAttributeDefinitions} = useAppConfig()
    // WHY: canProceedFrom(ORGANIZATION) returns true unconditionally because
    // the hook has no access to the form layout. Compute the layout-required
    // check here, where we have both the layout and the active definitions,
    // and AND it into canProceed when on the ORGANIZATION step. Without this
    // the wizard cheerfully submits a partyAttributes payload missing
    // displayName-template attrs and the backend rejects with PT0001.
    // Existing-party mode bypasses createParty server-side, so the required
    // check doesn't apply — the backend reuses the existing displayName.
    const organizationRequiredAttrsFilled = step !== OnboardingStep.ORGANIZATION
            || form.existingPartyId !== null
            || requiredAttrsAreFilled(form.partyAttributes, partyAttributeLayout, partyAttributeDefinitions)

    // WHY: the IDENTITY step has NO frontend layout-required gate because
    // the dev `identity-attribute` layout marks identity-level firstName
    // / lastName (def IDs aaaaaa00-…-0041 / -0042) as `requiredOverride:
    // required`, but the wizard collects firstName / lastName at the
    // PARTY level (def IDs aaaaaa01-…-0050 / -0051). A naive
    // `requiredAttrsAreFilled(form.identityAttributes,
    // identityAttributeLayout, …)` therefore always returns false and
    // the Next button is forever disabled (issue 1068). The backend's
    // type-scoped required-attribute gate (OnboardingService → EntitySchema
    // .missingRequired(Set, IdentityTypeKey)) is the canonical contract;
    // resolving the identity-vs-party overlap at the wizard level needs a
    // separate seed cleanup.
    const isLastStep = step === TOTAL_STEPS - 1
    const canProceed = canProceedFrom(step) && !hasIdentifierDuplicate
            && organizationRequiredAttrsFilled
    const isSubmitting = submitMutation.isPending

    const wizardNavigation = (
        <>
            {form.dirty ? (
                <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    data-testid="btn-cancel"
                >
                    {i18n.onboarding.actions.cancel}
                </Button>
            ) : (
                <div />
            )}

            <InlineRow>
                {step > 0 && (
                    <Button
                        variant="outline"
                        onClick={handlePrevious}
                        disabled={isSubmitting}
                        data-testid="btn-previous"
                    >
                        {i18n.onboarding.actions.previous}
                    </Button>
                )}

                {isLastStep ? (
                    <Button
                        onClick={handleSubmit}
                        disabled={!canProceed || isSubmitting}
                        data-testid="btn-submit"
                    >
                        {isSubmitting && <ButtonSpinner/>}
                        {isSubmitting ? i18n.onboarding.actions.submitting : i18n.onboarding.actions.submit}
                    </Button>
                ) : (
                    <Button
                        onClick={handleNext}
                        disabled={!canProceed || isSubmitting}
                        data-testid="btn-next"
                    >
                        {i18n.onboarding.actions.next}
                    </Button>
                )}
            </InlineRow>
        </>
    )

    return (
        <PageLayout
            data-testid="onboarding-wizard-page"
            variant="wizard"
            icon={CONCEPT_ICON.onboarding}
            title={i18n.onboarding.title}
            description={<MutedText>{i18n.onboarding.subtitle}</MutedText>}
            wizardSteps={STEP_LABELS}
            wizardCurrentStep={step}
            wizardNavigation={wizardNavigation}
        >
            <Stack data-testid={`wizard-step-${step}`} className="min-h-[300px]">
                {step === 0 && (
                    <PersonStep
                        form={form}
                        onUpdate={update}
                        onUpdateMultiple={updateMultiple}
                        onSetTemplate={setTemplate}
                    />
                )}
                {step === 1 && (
                    <OrganizationStep
                        form={form}
                        onUpdateMultiple={updateMultiple}
                        onSetPartyAttributes={setPartyAttributes}
                    />
                )}
                {step === 2 && (
                    <IdentityStep
                        form={form}
                        onUpdate={update}
                        onSetIdentifiers={setIdentifiers}
                        onSetIdentityAttributes={setIdentityAttributes}
                        onIdentifierDuplicateChange={setHasIdentifierDuplicate}
                    />
                )}
                {step === 3 && (
                    <ReviewStep
                        form={form}
                        onGoToStep={setStep}
                        submitError={submitMutation.error}
                    />
                )}
            </Stack>

            {/* Cancel confirmation dialog */}
            <Dialog open={cancelDialog.isOpen} onOpenChange={cancelDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.onboarding.cancel.title}</DialogTitle>
                        <DialogDescription>{i18n.onboarding.cancel.description}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="dialog"
                        onCancel={cancelDialog.close}
                        cancelLabel={i18n.onboarding.cancel.keepEditing}
                        saveLabel={i18n.onboarding.cancel.discard}
                        destructive
                        saveType="button"
                        onSave={handleDiscard}
                    />
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
